import { useEffect, useState } from "react";
import { adminListLeads, adminUpdateLead, adminDeleteLead } from "../../lib/api";
import { Phone, Mail, MapPin, Calendar, Trash2, RefreshCw } from "lucide-react";

const STATUSES = ["new", "contacted", "qualified", "closed"];

export default function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = () => {
    setLoading(true);
    adminListLeads().then(setLeads).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const updateStatus = async (id, status) => {
    await adminUpdateLead(id, { status });
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await adminDeleteLead(id);
    setLeads((ls) => ls.filter((l) => l.id !== id));
  };

  const filtered = filter === "all" ? leads : leads.filter((l) => l.status === filter);

  const counts = STATUSES.reduce((a, s) => ({ ...a, [s]: leads.filter((l) => l.status === s).length }), { all: leads.length });

  return (
    <div data-testid="admin-leads-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-display font-bold text-forest">Leads</h1>
          <p className="text-charcoal-soft text-sm mt-1">All inbound case review submissions.</p>
        </div>
        <button onClick={load} className="btn-ghost text-sm" data-testid="leads-refresh">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <StatCard label="All" value={counts.all} active={filter === "all"} onClick={() => setFilter("all")} />
        {STATUSES.map((s) => (
          <StatCard key={s} label={s} value={counts[s] || 0} active={filter === s} onClick={() => setFilter(s)} />
        ))}
      </div>

      {loading ? (
        <p className="text-charcoal-soft">Loading...</p>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-black/5">
          <p className="text-charcoal-soft">No leads yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((l) => (
            <div key={l.id} className="bg-white rounded-xl border border-black/5 p-5" data-testid={`lead-row-${l.id}`}>
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-display font-bold text-lg text-forest">{l.full_name || "(no name)"}</h3>
                    <select
                      value={l.status || "new"}
                      onChange={(e) => updateStatus(l.id, e.target.value)}
                      className={`text-xs font-bold uppercase px-2 py-1 rounded-full border ${
                        l.status === "new" ? "bg-terracotta/10 text-terracotta border-terracotta/30" :
                        l.status === "qualified" ? "bg-forest/10 text-forest border-forest/30" :
                        "bg-bone-alt text-charcoal-soft border-black/10"
                      }`}
                      data-testid={`lead-status-${l.id}`}
                    >
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-charcoal-soft mb-2">
                    {l.phone && <a href={`tel:${l.phone}`} className="flex items-center gap-1.5 hover:text-terracotta"><Phone className="w-3.5 h-3.5" /> {l.phone}</a>}
                    {l.email && <a href={`mailto:${l.email}`} className="flex items-center gap-1.5 hover:text-terracotta"><Mail className="w-3.5 h-3.5" /> {l.email}</a>}
                    {l.city && <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {l.city}</span>}
                    <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {new Date(l.created_at).toLocaleString()}</span>
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs">
                    {l.accident_type && <span className="px-2 py-0.5 rounded-full bg-forest/5 text-forest font-semibold">{l.accident_type}</span>}
                    {l.injuries && <span className="px-2 py-0.5 rounded-full bg-bone-alt">Injuries: {l.injuries}</span>}
                    {l.has_attorney && <span className="px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800">Has attorney</span>}
                    {l.source_page && <span className="px-2 py-0.5 rounded-full bg-bone-alt text-charcoal-soft">From: {l.source_page}</span>}
                  </div>
                  {l.description && (
                    <p className="text-sm text-charcoal-soft mt-3 italic">"{l.description}"</p>
                  )}
                </div>
                <button
                  onClick={() => remove(l.id)}
                  className="text-red-500 hover:bg-red-50 p-2 rounded-lg transition"
                  data-testid={`lead-delete-${l.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`p-4 rounded-xl border text-left transition ${active ? "bg-forest text-bone border-forest" : "bg-white border-black/5 hover:border-forest/30"}`}
      data-testid={`leads-filter-${label.toLowerCase()}`}
    >
      <div className="text-xs uppercase tracking-wider opacity-70 font-bold">{label}</div>
      <div className="font-display font-bold text-2xl mt-1">{value}</div>
    </button>
  );
}
